import {
    ComboBox,
    Content,
    Dialog,
    DialogContainer,
    Flex,
    Heading,
    Item,
    Link,
    TextField,
} from "@adobe/react-spectrum";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./invoice_bank_section.module.css";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import { GetContactRequest } from "../../../../../services/soap/main/requests/GetContactRequest";
import PaymentMethodDropDown from "./formComponents/PaymentMethodDropDown";

interface IInvoiceBankSection {
    invoice: any;
    isEdit: any;
    onChange: (data: any) => void;
}

type BankItem = {
    id: string; // unique key
    account: string;
    bankCode: string;
    bankIBAN: string;
    bankName: string; // label (we'll show bankNoteDomestic in dropdown)
    bankNoteDomestic: string;
    bankNoteForeign: string;
    swiftCode: string; // BIC
    isPrimary: string; // "1"/"0"
};

const InvoiceBankSection: React.FC<IInvoiceBankSection> = ({
    invoice,
    isEdit,
    onChange,
}) => {
    const [showMorefileds, setShowMorefields] = useState<boolean>(false);
    const [bankList, setBankList] = useState<BankItem[]>([]);
    const [selectedBankId, setSelectedBankId] = useState<string | null>(null);

    // ✅ for React Spectrum ComboBox custom typing
    const [bankInputValue, setBankInputValue] = useState<string>(
        invoice?.bankNoteDomestic ?? ""
    );

    const { t } = useTranslation("invoice");
    const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });
    const _tt = (key: string) => t(`invoice_common.${key}`, { ns: "invoice" });

    const { store, mainService } = useDependency();
    const lastProviderRef = useRef<string>("");

    const handleShowMorefields = () => {
        setShowMorefields(!showMorefileds);
    };

    const buildBankId = (b: any) => {
        const account = String(b?.account ?? "");
        const bankCode = String(b?.bankCode ?? "");
        const iban = String(b?.bankIBAN ?? "");
        return `${account}|${bankCode}|${iban}`;
    };

    const normalizeBanks = (banksWrapper: any): any[] => {
        if (!banksWrapper) return [];

        let raw = banksWrapper;

        // BANKS: { BANK: ... }
        if (typeof raw === "object" && !Array.isArray(raw) && raw.BANK) {
            raw = raw.BANK;
        }

        const list = Array.isArray(raw) ? raw : [raw];

        // elements can be { BANK: {...} } or direct {...}
        return list.map((x) => x?.BANK ?? x).filter(Boolean);
    };

    const mapToBankItem = (bank: any): BankItem => ({
        id: buildBankId(bank),
        account: String(bank?.account ?? ""),
        bankCode: String(bank?.bankCode ?? ""),
        bankIBAN: String(bank?.bankIBAN ?? ""),
        bankName: String(bank?.bankName ?? bank?.name ?? ""),
        bankNoteDomestic: String(bank?.bankNoteDomestic ?? ""),
        bankNoteForeign: String(bank?.bankNoteForeign ?? ""),
        swiftCode: String(bank?.swiftCode ?? ""),
        isPrimary: String(bank?.isPrimary ?? "0"),
    });

    const pickDefaultBank = (banks: BankItem[]) => {
        if (!banks.length) return null;
        const primary = banks.find((b) => String(b.isPrimary) === "1");
        return primary ?? banks[0];
    };

    // ✅ Only clear when supplier is removed (providerId empty)
    const clearInvoiceBankFields = () => {
        onChange({
            ...invoice,
            bankAccount: "",
            bankCode: "",
            bankIBAN: "",
            bankBic: "",
            bankNoteDomestic: "",
            bankNoteForeign: "",
            // keep these fields
            constantSymbol: invoice?.constantSymbol ?? "",
            paymentMethod: invoice?.paymentMethod ?? "",
        });
        setBankInputValue("");
    };

    const applyBankToInvoice = (bank: BankItem) => {
        onChange({
            ...invoice,
            bankAccount: bank.account,
            bankCode: bank.bankCode,
            bankIBAN: bank.bankIBAN,
            bankBic: bank.swiftCode,
            bankNoteDomestic: bank.bankNoteDomestic,
            bankNoteForeign: bank.bankNoteForeign,
        });

        // ✅ dropdown should show domestic bank note as label/value
        setBankInputValue(bank.bankNoteDomestic ?? "");
    };

    const loadBanksForProvider = async (providerId: string) => {
        // ✅ supplier removed => clear
        if (!providerId) {
            setBankList([]);
            setSelectedBankId(null);
            clearInvoiceBankFields();
            return;
        }

        const response: any = await mainService.getContact(
            new GetContactRequest(
                store.Server,
                store.SessionId,
                undefined,
                undefined,
                undefined,
                +providerId
            )
        );

        if (response?.result !== "OK") return;

        const entity = response?.COMPANY ?? response?.PERSON;
        const banksRaw = entity?.BANKS;

        const banks = normalizeBanks(banksRaw).map(mapToBankItem);
        setBankList(banks);

        // ✅ 0 banks => allow manual entry (DO NOT wipe fields)
        if (!banks.length) {
            setSelectedBankId(null);
            // keep whatever user already typed (invoice.bankNoteDomestic)
            setBankInputValue(String(invoice?.bankNoteDomestic ?? ""));
            return;
        }

        // ✅ match invoice existing bank OR pick default
        const desiredId = `${String(invoice?.bankAccount ?? "")}|${String(
            invoice?.bankCode ?? ""
        )}|${String(invoice?.bankIBAN ?? "")}`;

        const match = banks.find((b) => b.id === desiredId) ?? null;
        const fallback = pickDefaultBank(banks);
        const toSelect = match ?? fallback;

        setSelectedBankId(toSelect ? toSelect.id : null);

        // ✅ auto apply only when not edit
        if (!isEdit && toSelect) {
            applyBankToInvoice(toSelect);
        } else {
            // edit mode -> keep input as invoice value
            setBankInputValue(String(invoice?.bankNoteDomestic ?? ""));
        }
    };

    useEffect(() => {
        const providerId = String(invoice?.provider ?? "");

        if (lastProviderRef.current === providerId) return;
        lastProviderRef.current = providerId;

        loadBanksForProvider(providerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice?.provider]);

    // ✅ if invoice.bankNoteDomestic changes from outside, keep ComboBox input synced (only when no bank selected)
    useEffect(() => {
        if (!selectedBankId) {
            setBankInputValue(String(invoice?.bankNoteDomestic ?? ""));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice?.bankNoteDomestic]);

    const selectedBank = useMemo(() => {
        return bankList.find((b) => b.id === selectedBankId) ?? null;
    }, [bankList, selectedBankId]);

    const onBankSelectionChange = (key: React.Key | null) => {
        const idStr = key ? String(key) : null;
        setSelectedBankId(idStr);

        const bank = idStr ? bankList.find((b) => b.id === idStr) ?? null : null;

        // ✅ cleared => allow manual entry, don't wipe fields
        if (!bank) return;

        applyBankToInvoice(bank);
    };

    // ✅ user types bank name (bankNoteDomestic) even if there are 0 banks
    const onBankInputChange = (val: string) => {
        setBankInputValue(val);
        onChange({ ...invoice, bankNoteDomestic: val });
    };

    function BankDetailsModal() {
        return (
            <>
                <Dialog
                    width={{ L: "800px", base: "100%" }}
                    aria-label="Bank Info"
                    isDismissable
                    onDismiss={() => setShowMorefields(false)}
                >
                    <Heading level={2}>{_t("bank_info")}</Heading>

                    <Content>
                        <Flex
                            direction={{ base: "column", L: "row" }}
                            gap={"size-150"}
                            marginTop={"size-150"}
                        >
                            <TextField
                                width={"100%"}
                                label="Bank Domestic"
                                value={invoice?.bankNoteDomestic ?? ""}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, bankNoteDomestic: e })
                                }
                            />
                            <TextField
                                width={"100%"}
                                label="Bank Foreign"
                                value={invoice?.bankNoteForeign ?? ""}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, bankNoteForeign: e })
                                }
                            />
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-100"}>
                            {/* ✅ Bank dropdown shows bankNoteDomestic and allows typing */}
                            <ComboBox
                                label={_t("payment")}
                                aria-label={_t("payment")}
                                items={bankList}
                                selectedKey={selectedBankId}
                                inputValue={bankInputValue}
                                onInputChange={onBankInputChange}
                                onSelectionChange={onBankSelectionChange}
                                allowsCustomValue
                                width={{ base: "100%", L: "80%" }}
                            >
                                {(item: any) => (
                                    <Item key={item.id} textValue={item.bankNoteDomestic}>
                                        {item.bankNoteDomestic}
                                    </Item>
                                )}
                            </ComboBox>

                            <TextField
                                label="Bank Code"
                                width={"20%"}
                                value={invoice?.bankCode ?? ""}
                                onChange={(e) => onChange({ ...invoice, bankCode: e })}
                            />

                            <TextField
                                label="Bank IBAN"
                                width={"100%"}
                                value={invoice?.bankIBAN ?? ""}
                                onChange={(e) => onChange({ ...invoice, bankIBAN: e })}
                            />
                        </Flex>

                        <Flex direction={{ base: "column", L: "row" }} gap={"size-150"}>
                            <ComboBox
                                label="Constant symbol"
                                width={{ base: "100%", L: "50%" }}
                                selectedKey={invoice?.constantSymbol}
                                items={[{ id: "1", bankName: "None" }]}
                                allowsCustomValue
                                onSelectionChange={(key) =>
                                    onChange({
                                        ...invoice,
                                        constantSymbol: key ? String(key) : "",
                                    })
                                }
                            >
                                {(item: any) => (
                                    <Item key={item.id} textValue={item.bankName}>
                                        {item.bankName}
                                    </Item>
                                )}
                            </ComboBox>

                            <PaymentMethodDropDown
                                selectedPaymentMethod={invoice?.paymentMethod}
                                paymentMethodName={invoice?.paymentMethodName}
                                onChange={(e: any) =>
                                    onChange({ ...invoice, paymentMethod: e?.id })
                                }
                                isEdit={isEdit}
                                width={"50%"}
                            />

                            <TextField
                                width={{ base: "100%", L: "100%" }}
                                label="BIC Code"
                                value={invoice?.bankBic ?? ""}
                                onChange={(e: any) => onChange({ ...invoice, bankBic: e })}
                            />
                        </Flex>
                    </Content>
                </Dialog>

                <Flex
                    UNSAFE_style={{
                        backgroundColor: "white",
                        justifyContent: "end",
                        width: "100%",
                        padding: "1rem",
                        paddingLeft: 55,
                        paddingRight: 50,
                        boxShadow: "0 -1px 1px rgba(0,0,0,0.1)",
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                    }}
                >
                    <Flex gap={"size-200"} UNSAFE_className={styles.button_container}>
                        <Button
                            variant="danger"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_cancel}`}
                        >
                            {_tt("button_cancel")}
                        </Button>
                        <Button
                            type="submit"
                            onClick={() => setShowMorefields(false)}
                            className={`${styles.btn} ${styles.btn_add}`}
                        >
                            {_tt("button_ok")}
                        </Button>
                    </Flex>
                </Flex>
            </>
        );
    }

    return (
        <Flex
            direction={{ base: "column", L: "column" }}
            UNSAFE_className={styles.main_container}
        >
            <Flex justifyContent={"end"}>
                <Link
                    UNSAFE_className={styles.show_more_button}
                    onPress={handleShowMorefields}
                    isQuiet
                >
                    Show more
                </Link>
            </Flex>

            <Flex gap={"size-100"}>
                {/* ✅ Bank dropdown shows bankNoteDomestic and allows typing even if list is empty */}
                <ComboBox
                    label={_t("payment")}
                    aria-label={_t("payment")}
                    items={bankList}
                    selectedKey={selectedBankId}
                    inputValue={bankInputValue}
                    onInputChange={onBankInputChange}
                    onSelectionChange={onBankSelectionChange}
                    allowsCustomValue
                    width={{ base: "100%", L: "100%" }}
                >
                    {(item: any) => (
                        <Item key={item.id} textValue={item.bankNoteDomestic}>
                            {item.bankNoteDomestic}
                        </Item>
                    )}
                </ComboBox>

                <TextField
                    label="Bank Code"
                    width={"20%"}
                    value={invoice?.bankCode ?? ""}
                    onChange={(e) => onChange({ ...invoice, bankCode: e })}
                />
            </Flex>

            <DialogContainer isDismissable onDismiss={() => setShowMorefields(false)}>
                {showMorefileds && <BankDetailsModal />}
            </DialogContainer>
        </Flex>
    );
};

export default InvoiceBankSection;
